/**
 * Ficha/editor de presupuesto. Cubre /quotes/new y /quotes/:id.
 *
 * En BORRADOR todo es editable (cliente, expediente, fechas, líneas, IRPF).
 * En el resto de estados se muestra en solo lectura con las acciones que
 * correspondan: PDF, enviar por email, aceptar/rechazar, convertir a factura.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  AlertCircle,
  CalendarClock,
  Check,
  ChevronLeft,
  Coins,
  FileDown,
  FileSignature,
  Loader2,
  Mail,
  Plus,
  Receipt,
  Save,
  Send,
  Trash2,
  X,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { formatCents, parseEurosToCents, centsToEurosInput } from '../lib/money'
import Modal from '../components/ui/Modal'
import RatePickerModal from '../components/rates/RatePickerModal'
import MilestonesPanel, { PaymentPlanModal } from '../components/billing/MilestonesPanel'
import { QuoteStatusBadge } from './Quotes'
import {
  type CaseListItem,
  type ClientListResponse,
  type QuoteDetail,
  type QuoteLineInput,
} from '@toga/shared'

interface LineDraft {
  key: number
  description: string
  quantity: string
  unitPriceEur: string
  discountPctInput: string // "10" = 10 %
  vatPctInput: string // "21" = 21 %
  isSupplied: boolean
}

let lineKeySeq = 1

function emptyLine(): LineDraft {
  return {
    key: lineKeySeq++,
    description: '',
    quantity: '1',
    unitPriceEur: '',
    discountPctInput: '0',
    vatPctInput: '21',
    isSupplied: false,
  }
}

function lineToDraft(l: QuoteDetail['lines'][number]): LineDraft {
  return {
    key: lineKeySeq++,
    description: l.description,
    quantity: l.quantity,
    unitPriceEur: centsToEurosInput(l.unitPriceCents),
    discountPctInput: (Number(l.discountPct) * 100).toString(),
    vatPctInput: (Number(l.vatRate) * 100).toString(),
    isSupplied: l.isSupplied,
  }
}

function draftToInput(l: LineDraft, position: number): QuoteLineInput {
  return {
    position,
    description: l.description.trim(),
    quantity: l.quantity || '1',
    unitPriceCents: parseEurosToCents(l.unitPriceEur),
    discountPct: String((Number(l.discountPctInput) || 0) / 100),
    vatRate: String((Number(l.vatPctInput) || 0) / 100),
    isSupplied: l.isSupplied,
  }
}

function computeDraftTotals(lines: LineDraft[], irpfPct: number) {
  let subtotal = 0
  let vat = 0
  let irpfBase = 0
  for (const l of lines) {
    const qty = Number(l.quantity) || 0
    const unit = parseEurosToCents(l.unitPriceEur)
    const disc = (Number(l.discountPctInput) || 0) / 100
    const net = Math.round(qty * unit * (1 - disc))
    subtotal += net
    if (!l.isSupplied) {
      vat += Math.round((net * (Number(l.vatPctInput) || 0)) / 100)
      irpfBase += net
    }
  }
  const irpf = Math.round((irpfBase * irpfPct) / 100)
  return { subtotal, vat, irpf, total: subtotal + vat - irpf }
}

const inputCls =
  'w-full px-2.5 py-1.5 rounded-lg bg-slate-900/40 border border-base text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50 disabled:opacity-60'

export default function QuoteEditorPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = !id
  const navigate = useNavigate()
  // A1: «Nuevo presupuesto» desde la ficha de un cliente llega con ?clientId=
  const presetClientId = useSearchParams()[0].get('clientId') ?? ''

  const [detail, setDetail] = useState<QuoteDetail | null>(null)
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [actionBusy, setActionBusy] = useState<string | null>(null)

  // Form state
  const [clients, setClients] = useState<Array<{ id: string; displayName: string; email: string | null }>>([])
  const [cases, setCases] = useState<CaseListItem[]>([])
  const [clientId, setClientId] = useState(isNew ? presetClientId : '')
  const [caseId, setCaseId] = useState('')
  const [title, setTitle] = useState('')
  const [issueDate, setIssueDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [validUntil, setValidUntil] = useState('')
  const [irpfPctInput, setIrpfPctInput] = useState('0')
  const [notes, setNotes] = useState('')
  const [internalNotes, setInternalNotes] = useState('')
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()])

  const [ratePickerOpen, setRatePickerOpen] = useState(false)
  const [planOpen, setPlanOpen] = useState(false)
  const [milestonesKey, setMilestonesKey] = useState(0)
  const [sendOpen, setSendOpen] = useState(false)
  const [sendTo, setSendTo] = useState('')
  const [sendMessage, setSendMessage] = useState('')

  const editable = isNew || detail?.status === 'BORRADOR'

  // Clientes para el selector
  useEffect(() => {
    api
      .get<ClientListResponse>('/clients?pageSize=100')
      .then(async (r) => {
        const list = r.items.map((c) => ({
          id: c.id,
          displayName: c.displayName,
          email: c.email ?? null,
        }))
        // El cliente precargado puede quedar fuera de los 100 primeros: se pide aparte.
        if (isNew && presetClientId && !list.some((c) => c.id === presetClientId)) {
          try {
            const { client } = await api.get<{ client: { id: string; displayName: string; email: string | null } }>(
              `/clients/${presetClientId}`
            )
            list.unshift({ id: client.id, displayName: client.displayName, email: client.email ?? null })
          } catch {
            // Si no existe, el selector simplemente queda sin preselección.
          }
        }
        setClients(list)
      })
      .catch(() => setClients([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Expedientes del cliente seleccionado
  useEffect(() => {
    if (!clientId) {
      setCases([])
      return
    }
    api
      .get<{ items: CaseListItem[] }>(`/cases?clientId=${clientId}&pageSize=100`)
      .then((r) => setCases(r.items))
      .catch(() => setCases([]))
  }, [clientId])

  const applyDetail = useCallback((q: QuoteDetail) => {
    setDetail(q)
    setClientId(q.clientId)
    setCaseId(q.caseId ?? '')
    setTitle(q.title ?? '')
    setIssueDate(q.issueDate.slice(0, 10))
    setValidUntil(q.validUntil ? q.validUntil.slice(0, 10) : '')
    setIrpfPctInput((Number(q.irpfRate) * 100).toString())
    setNotes(q.notes ?? '')
    setInternalNotes(q.internalNotes ?? '')
    setLines(q.lines.length > 0 ? q.lines.map(lineToDraft) : [emptyLine()])
    setSendTo(q.sentTo ?? '')
  }, [])

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const { quote } = await api.get<{ quote: QuoteDetail }>(`/quotes/${id}`)
      applyDetail(quote)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar')
    } finally {
      setLoading(false)
    }
  }, [id, applyDetail])

  useEffect(() => {
    void load()
  }, [load])

  const totals = useMemo(
    () => computeDraftTotals(lines, Number(irpfPctInput) || 0),
    [lines, irpfPctInput]
  )

  const updLine = (key: number, patch: Partial<LineDraft>) => {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  }

  const validLines = lines.filter((l) => l.description.trim() && parseEurosToCents(l.unitPriceEur) !== 0)

  const onSave = async () => {
    if (!clientId) {
      setError('Selecciona un cliente')
      return
    }
    if (validLines.length === 0) {
      setError('Añade al menos una línea con descripción e importe')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const payload = {
        clientId,
        caseId: caseId || null,
        title: title.trim() || null,
        notes: notes.trim() || null,
        internalNotes: internalNotes.trim() || null,
        issueDate: new Date(`${issueDate}T12:00:00`).toISOString(),
        validUntil: validUntil ? new Date(`${validUntil}T23:59:59`).toISOString() : null,
        irpfRate: String((Number(irpfPctInput) || 0) / 100),
        lines: validLines.map((l, i) => draftToInput(l, i)),
      }
      if (isNew) {
        const { quote } = await api.post<{ quote: QuoteDetail }>('/quotes', payload)
        navigate(`/quotes/${quote.id}`, { replace: true })
        applyDetail(quote)
      } else {
        const { quote } = await api.patch<{ quote: QuoteDetail }>(`/quotes/${id}`, payload)
        applyDetail(quote)
      }
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error al guardar'))
    } finally {
      setSaving(false)
    }
  }

  const runAction = async (key: string, fn: () => Promise<void>) => {
    setActionBusy(key)
    setError(null)
    try {
      await fn()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setActionBusy(null)
    }
  }

  const onTransition = (action: 'ACEPTAR' | 'RECHAZAR' | 'MARCAR_ENVIADO') =>
    runAction(action, async () => {
      const { quote } = await api.post<{ quote: QuoteDetail }>(`/quotes/${id}/transition`, {
        action,
      })
      applyDetail(quote)
    })

  const onSend = () =>
    runAction('SEND', async () => {
      const { quote } = await api.post<{ quote: QuoteDetail }>(`/quotes/${id}/send`, {
        ...(sendTo.trim() ? { to: sendTo.trim() } : {}),
        ...(sendMessage.trim() ? { message: sendMessage.trim() } : {}),
      })
      applyDetail(quote)
      setSendOpen(false)
    })

  const onToInvoice = () =>
    runAction('INVOICE', async () => {
      const { invoiceId } = await api.post<{ invoiceId: string }>(`/quotes/${id}/to-invoice`)
      navigate(`/billing/${invoiceId}`)
    })

  const onDelete = () =>
    runAction('DELETE', async () => {
      if (!window.confirm('¿Eliminar este presupuesto?')) return
      await api.delete(`/quotes/${id}`)
      navigate('/quotes')
    })

  const clientEmail = clients.find((c) => c.id === clientId)?.email ?? null
  const selectedCase = cases.find((c) => c.id === caseId)

  if (loading) {
    return (
      <div className="t-muted flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Cargando…
      </div>
    )
  }

  // No encontrado / sin permiso: no mostramos un editor vacío editable.
  if (!isNew && !detail) {
    return (
      <div className="surface p-8 text-center max-w-lg mx-auto animate-slideUp">
        <AlertCircle size={28} className="mx-auto text-rose-300 mb-2" />
        <p className="t-primary font-medium mb-1">Presupuesto no disponible</p>
        <p className="t-muted text-sm mb-4">
          {error ?? 'No se ha podido cargar este presupuesto.'}
        </p>
        <button
          type="button"
          onClick={() => navigate('/quotes')}
          className="px-3 py-1.5 rounded-lg text-sm border border-base hover:bg-hover t-secondary"
        >
          ← Volver a presupuestos
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4 animate-slideUp max-w-5xl">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/quotes" className="t-muted hover:text-toga-300">
            <ChevronLeft size={18} />
          </Link>
          <div>
            <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
              <FileSignature size={20} className="text-toga-300" />
              {isNew ? 'Nuevo presupuesto' : detail?.number}
              {detail && <QuoteStatusBadge status={detail.status} />}
            </h1>
            {detail?.sentAt && (
              <p className="text-xs t-muted">
                Enviado el {new Date(detail.sentAt).toLocaleString('es-ES')}
                {detail.sentTo ? ` a ${detail.sentTo}` : ''}
              </p>
            )}
            {detail?.invoiceId && (
              <p className="text-xs t-muted">
                Factura:{' '}
                <Link to={`/billing/${detail.invoiceId}`} className="text-toga-300 hover:underline">
                  {detail.invoiceNumber ?? 'borrador'}
                </Link>
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {!isNew && (
            <button
              type="button"
              onClick={() => window.open(`/api/quotes/${id}/pdf`, '_blank')}
              className="px-3 py-1.5 rounded-lg text-sm border border-base hover:bg-hover t-secondary flex items-center gap-1.5"
            >
              <FileDown size={14} /> PDF
            </button>
          )}
          {!isNew && detail && detail.status !== 'FACTURADO' && (
            <button
              type="button"
              onClick={() => {
                setSendTo(detail.sentTo ?? clientEmail ?? '')
                setSendOpen(true)
              }}
              className="px-3 py-1.5 rounded-lg text-sm border border-base hover:bg-hover t-secondary flex items-center gap-1.5"
            >
              <Mail size={14} /> Enviar por email
            </button>
          )}
          {detail?.status === 'BORRADOR' && (
            <button
              type="button"
              disabled={actionBusy !== null}
              onClick={() => void onTransition('MARCAR_ENVIADO')}
              className="px-3 py-1.5 rounded-lg text-sm border border-base hover:bg-hover t-secondary flex items-center gap-1.5"
              title="Marcar como enviado sin mandar email"
            >
              <Send size={14} /> Marcar enviado
            </button>
          )}
          {detail && ['BORRADOR', 'ENVIADO', 'CADUCADO'].includes(detail.status) && (
            <>
              <button
                type="button"
                disabled={actionBusy !== null}
                onClick={() => void onTransition('ACEPTAR')}
                className="px-3 py-1.5 rounded-lg text-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-1.5"
              >
                <Check size={14} /> Aceptar
              </button>
              <button
                type="button"
                disabled={actionBusy !== null}
                onClick={() => void onTransition('RECHAZAR')}
                className="px-3 py-1.5 rounded-lg text-sm border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 flex items-center gap-1.5"
              >
                <X size={14} /> Rechazar
              </button>
            </>
          )}
          {detail?.status === 'ACEPTADO' && (
            <>
              <button
                type="button"
                disabled={actionBusy !== null}
                onClick={() => setPlanOpen(true)}
                className="px-3 py-1.5 rounded-lg text-sm border border-teal-500/40 text-teal-300 hover:bg-teal-500/10 flex items-center gap-1.5"
                title="Repartir el importe en hitos de pago (facturación parcial)"
              >
                <CalendarClock size={14} /> Plan de pagos
              </button>
              <button
                type="button"
                disabled={actionBusy !== null}
                onClick={() => void onToInvoice()}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
                title="Facturar el presupuesto completo de una vez"
              >
                {actionBusy === 'INVOICE' ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Receipt size={14} />
                )}
                Convertir a factura
              </button>
            </>
          )}
          {editable && (
            <button
              type="button"
              disabled={saving}
              onClick={() => void onSave()}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Guardar
            </button>
          )}
          {!isNew && detail && detail.status !== 'FACTURADO' && (
            <button
              type="button"
              disabled={actionBusy !== null}
              onClick={() => void onDelete()}
              className="w-8 h-8 grid place-items-center rounded-lg border border-base hover:border-rose-500/40 hover:text-rose-300 t-muted"
              title="Eliminar presupuesto"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {/* Datos generales */}
      <div className="surface p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="col-span-2">
          <label className="block text-xs t-muted mb-1">Cliente</label>
          <select
            disabled={!editable}
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value)
              setCaseId('')
            }}
            className={inputCls}
          >
            <option value="">— Selecciona cliente —</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName}
              </option>
            ))}
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-xs t-muted mb-1">Expediente (opcional)</label>
          <select
            disabled={!editable || !clientId}
            value={caseId}
            onChange={(e) => setCaseId(e.target.value)}
            className={inputCls}
          >
            <option value="">— Sin expediente —</option>
            {cases.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} · {c.title}
              </option>
            ))}
          </select>
        </div>
        <div className="col-span-2 md:col-span-4">
          <label className="block text-xs t-muted mb-1">Concepto</label>
          <input
            disabled={!editable}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="P. ej. Defensa en procedimiento ordinario de reclamación de cantidad"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">Fecha</label>
          <input
            type="date"
            disabled={!editable}
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">Válido hasta</label>
          <input
            type="date"
            disabled={!editable}
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">Retención IRPF (%)</label>
          <input
            type="number"
            min={0}
            max={47}
            step="0.01"
            disabled={!editable}
            value={irpfPctInput}
            onChange={(e) => setIrpfPctInput(e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      {/* Líneas */}
      <div className="surface p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="t-primary font-semibold text-sm">Líneas</h3>
          {editable && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!clientId}
                onClick={() => setRatePickerOpen(true)}
                className="px-2.5 py-1.5 rounded-lg text-xs border border-base hover:bg-hover t-secondary flex items-center gap-1.5 disabled:opacity-40"
                title={clientId ? 'Insertar línea desde el catálogo de tarifas' : 'Selecciona primero un cliente'}
              >
                <Coins size={13} /> Desde tarifa
              </button>
              <button
                type="button"
                onClick={() => setLines((ls) => [...ls, emptyLine()])}
                className="px-2.5 py-1.5 rounded-lg text-xs border border-base hover:bg-hover t-secondary flex items-center gap-1.5"
              >
                <Plus size={13} /> Añadir línea
              </button>
            </div>
          )}
        </div>

        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider t-faint">
            <tr>
              <th className="text-left py-1">Descripción</th>
              <th className="text-right w-20">Cant.</th>
              <th className="text-right w-28">Precio (€)</th>
              <th className="text-right w-20">Dto. %</th>
              <th className="text-right w-20">IVA %</th>
              <th className="text-center w-16" title="Suplido: sin IVA ni IRPF">Supl.</th>
              <th className="text-right w-28">Total</th>
              {editable && <th className="w-8" />}
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => {
              const qty = Number(l.quantity) || 0
              const unit = parseEurosToCents(l.unitPriceEur)
              const disc = (Number(l.discountPctInput) || 0) / 100
              const net = Math.round(qty * unit * (1 - disc))
              const vat = l.isSupplied ? 0 : Math.round((net * (Number(l.vatPctInput) || 0)) / 100)
              return (
                <tr key={l.key} className="border-t border-base/40">
                  <td className="py-1.5 pr-2">
                    <input
                      disabled={!editable}
                      value={l.description}
                      onChange={(e) => updLine(l.key, { description: e.target.value })}
                      placeholder="Descripción del servicio"
                      className={inputCls}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <input
                      disabled={!editable}
                      value={l.quantity}
                      onChange={(e) => updLine(l.key, { quantity: e.target.value })}
                      className={`${inputCls} text-right`}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <input
                      disabled={!editable}
                      value={l.unitPriceEur}
                      onChange={(e) => updLine(l.key, { unitPriceEur: e.target.value })}
                      placeholder="0,00"
                      className={`${inputCls} text-right`}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <input
                      disabled={!editable}
                      value={l.discountPctInput}
                      onChange={(e) => updLine(l.key, { discountPctInput: e.target.value })}
                      className={`${inputCls} text-right`}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <input
                      disabled={!editable}
                      value={l.vatPctInput}
                      onChange={(e) => updLine(l.key, { vatPctInput: e.target.value })}
                      className={`${inputCls} text-right`}
                    />
                  </td>
                  <td className="py-1.5 text-center">
                    <input
                      type="checkbox"
                      disabled={!editable}
                      checked={l.isSupplied}
                      onChange={(e) => updLine(l.key, { isSupplied: e.target.checked })}
                    />
                  </td>
                  <td className="py-1.5 text-right font-mono t-secondary">
                    {formatCents(net + vat)}
                  </td>
                  {editable && (
                    <td className="py-1.5 text-right">
                      <button
                        type="button"
                        onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                        className="t-faint hover:text-rose-300"
                        title="Quitar línea"
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>

        {/* Totales */}
        <div className="flex justify-end">
          <div className="text-sm space-y-1 min-w-[260px]">
            <div className="flex justify-between t-secondary">
              <span>Base imponible</span>
              <span className="font-mono">{formatCents(totals.subtotal)}</span>
            </div>
            <div className="flex justify-between t-secondary">
              <span>IVA</span>
              <span className="font-mono">{formatCents(totals.vat)}</span>
            </div>
            {totals.irpf > 0 && (
              <div className="flex justify-between t-secondary">
                <span>Retención IRPF</span>
                <span className="font-mono">-{formatCents(totals.irpf)}</span>
              </div>
            )}
            <div className="flex justify-between t-primary font-semibold border-t border-base pt-1">
              <span>TOTAL</span>
              <span className="font-mono">{formatCents(totals.total)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Notas */}
      <div className="surface p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs t-muted mb-1">
            Condiciones (visibles en el PDF y el email)
          </label>
          <textarea
            disabled={!editable}
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Forma de pago, alcance, exclusiones…"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">Notas internas (no se envían)</label>
          <textarea
            disabled={!editable}
            rows={3}
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      {/* Plan de pagos del presupuesto */}
      {!isNew && detail && id && (
        <MilestonesPanel
          key={milestonesKey}
          clientId={detail.clientId}
          quoteId={id}
          canWrite
        />
      )}

      {/* Picker de tarifas */}
      <RatePickerModal
        open={ratePickerOpen}
        clientId={clientId || null}
        area={selectedCase?.area ?? null}
        onClose={() => setRatePickerOpen(false)}
        onPick={(item) => {
          setLines((ls) => {
            const next = ls.filter((l) => l.description.trim() || parseEurosToCents(l.unitPriceEur) !== 0)
            return [
              ...next,
              {
                key: lineKeySeq++,
                description: item.name + (item.description ? ` — ${item.description}` : ''),
                quantity: '1',
                unitPriceEur: centsToEurosInput(item.appliedAmountCents),
                discountPctInput: '0',
                vatPctInput: (item.vatRate * 100).toString(),
                isSupplied: false,
              },
            ]
          })
          setRatePickerOpen(false)
        }}
      />

      {/* Modal de plan de pagos */}
      {!isNew && detail && id && (
        <PaymentPlanModal
          open={planOpen}
          onClose={() => setPlanOpen(false)}
          clientId={detail.clientId}
          caseId={detail.caseId}
          quoteId={id}
          subtotalCents={detail.subtotalCents}
          onCreated={() => setMilestonesKey((k) => k + 1)}
        />
      )}

      {/* Modal de envío por email */}
      <Modal open={sendOpen} onClose={() => setSendOpen(false)} title="Enviar presupuesto por email">
        <div className="space-y-3">
          <div>
            <label className="block text-xs t-muted mb-1">Destinatario</label>
            <input
              value={sendTo}
              onChange={(e) => setSendTo(e.target.value)}
              placeholder={clientEmail ?? 'email@cliente.com'}
              className={inputCls}
            />
            {!clientEmail && !sendTo && (
              <p className="text-xs text-amber-300 mt-1">
                El cliente no tiene email guardado; indica el destinatario.
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs t-muted mb-1">Mensaje adicional (opcional)</label>
            <textarea
              rows={3}
              value={sendMessage}
              onChange={(e) => setSendMessage(e.target.value)}
              placeholder="Texto que se añadirá al cuerpo del email…"
              className={inputCls}
            />
          </div>
          <p className="text-xs t-faint">
            Se adjuntará el PDF del presupuesto. El envío usa el correo saliente configurado en
            Ajustes → Correo.
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setSendOpen(false)}
              className="px-3 py-1.5 rounded-lg text-sm border border-base hover:bg-hover t-secondary"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={actionBusy === 'SEND' || !sendTo.trim()}
              onClick={() => void onSend()}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5 disabled:opacity-50"
            >
              {actionBusy === 'SEND' ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Mail size={14} />
              )}
              Enviar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
